import { ChildInputType, Enum } from '~/data/enums';

export const getEnumObject = (
  enumList: Enum[],
  enumCode: string,
): Enum | undefined => {
  return enumList.find((e) => e.code === enumCode);
};

export const getEnumObjects = (enumList: Enum[], enumCodes: string[]) => {
  return enumCodes
    .map((code) => getEnumObject(enumList, code))
    .filter((e): e is Enum => !!e);
};

export const getEnumObjectByExtCode = (
  enumList: Enum[],
  extCode: string,
): Enum | undefined => {
  return enumList.find((e) => e.extCode === extCode);
};

export const getEnumObjectsByExtCodes = (
  enumList: Enum[],
  enumExtCodes: string[],
) => {
  return enumExtCodes
    .map((extCode) => getEnumObjectByExtCode(enumList, extCode))
    .filter((e): e is Enum => !!e);
};

export const getEnumName = (enumList: Enum[], enumCode: string): string =>
  getEnumObject(enumList, enumCode)?.name || '';

export const getEnumDescription = (
  enumList: Enum[],
  enumCode: string,
): string => getEnumObject(enumList, enumCode)?.description || '';

export const filterActiveEnums = (
  enumList: Enum[],
  exceptionCode?: string | string[],
): Enum[] => {
  return enumList.reduce((filteredEnums: Enum[], enumData: Enum) => {
    if (
      (Array.isArray(exceptionCode) &&
        exceptionCode?.includes(enumData.code)) ||
      enumData.isActive !== false
    ) {
      filteredEnums.push(enumData);
    } else if (
      (typeof exceptionCode === 'string' && exceptionCode === enumData.code) ||
      enumData.isActive !== false
    ) {
      filteredEnums.push(enumData);
    }
    return filteredEnums;
  }, []);
};

export const filterEnumsByCountryAndOrgUnits = (
  enumList: Enum[],
  countryId?: number | string,
  orgUnitId?: number | string,
  exceptionCode?: string | string[],
): Enum[] => {
  return enumList.reduce((filteredEnums: Enum[], enumData: Enum) => {
    if (
      (Array.isArray(exceptionCode) &&
        exceptionCode?.includes(enumData.code)) ||
      (typeof exceptionCode === 'string' && exceptionCode === enumData.code)
    ) {
      filteredEnums.push(enumData);
    } else {
      const isCountryRestricted = !!(
        enumData.countries && enumData.countries.length > 0
      );
      const isOrgUnitRestricted = !!(
        enumData.orgUnits && enumData.orgUnits.length > 0
      );

      if (!isCountryRestricted && !isOrgUnitRestricted) {
        filteredEnums.push(enumData);
      } else {
        const getEnumDataValueStrings = (
          enumDataList: string[] | ChildInputType[] | undefined,
        ): string[] => {
          return (enumDataList || []).map((el) => {
            if (typeof el === 'string') {
              return el;
            }
            return el.value.toString();
          });
        };

        const hasMatchOnList = (
          list: string[],
          value?: string | number,
        ): boolean => {
          return typeof value !== 'undefined'
            ? list.includes(value.toString())
            : false;
        };

        const countryMatch = hasMatchOnList(
          getEnumDataValueStrings(enumData.countries),
          countryId,
        );
        const orgUnitMatch = hasMatchOnList(
          getEnumDataValueStrings(enumData.orgUnits),
          orgUnitId,
        );

        if (
          isCountryRestricted === countryMatch &&
          isOrgUnitRestricted === orgUnitMatch
        ) {
          filteredEnums.push(enumData);
        }
      }
    }
    return filteredEnums;
  }, []);
};

export const filterActiveEnumsByCountryAndOrgUnits = (
  enumList: Enum[],
  countryId?: number | string,
  orgUnitId?: number | string,
  exceptionCode?: string | string[],
) => {
  return filterEnumsByCountryAndOrgUnits(
    filterActiveEnums(enumList, exceptionCode),
    countryId,
    orgUnitId,
    exceptionCode,
  );
};

export const sortEnumsBySeq = (enums: Enum[]): Enum[] =>
  enums.sort((a, b) => (a?.seq || 0) - (b?.seq || 0));
